import React, { useState } from 'react';
import { 
    Typography, Card, Form, Input, Button, Select, 
    Row, Col, Space, Alert, message, Divider, 
    Tabs, Badge, Empty
} from 'antd';
import { 
    BellOutlined, 
    SendOutlined, 
    GlobalOutlined, 
    UserOutlined,
    InfoCircleOutlined,
    CheckCircleOutlined,
    AlertOutlined,
    NotificationOutlined
} from '@ant-design/icons';
import api from '../../services/api';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const NotificationManagement = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);

    const onFinish = async (values) => {
        setLoading(true);
        try {
            const endpoint = values.target === 'all' ? '/admin/notifications/broadcast' : '/admin/notifications/send';
            const payload = {
                type: values.type,
                title: values.title,
                message: values.message,
                userId: values.target === 'user' ? values.userId : undefined,
                data: values.link ? { url: values.link } : null
            };

            const response = await api.post(endpoint, payload);
            if (response.data.success) {
                message.success(values.target === 'all' ? 'Broadcast envoyé à tous les utilisateurs !' : 'Notification envoyée avec succès !');
                form.resetFields(['title', 'message', 'link']);
            }
        } catch (error) {
            console.error('Send notification error:', error);
            message.error('Échec de l\'envoi de la notification');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="admin-notifications-page p-6">
            <Row gutter={[24, 24]}>
                <Col span={24}>
                    <Card className="premium-card bg-slate-900 border-white/5 shadow-2xl overflow-hidden">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="p-4 bg-indigo-500/10 rounded-2xl border border-indigo-500/20">
                                <BellOutlined className="text-2xl text-indigo-400" />
                            </div>
                            <div>
                                <Title level={2} style={{ margin: 0, color: 'white' }} className="uppercase tracking-tighter font-black">
                                    Centre de Notifications
                                </Title>
                                <Text className="text-slate-500 font-bold uppercase tracking-widest text-[10px]">
                                    Communication Directe & Broadcast Système
                                </Text>
                            </div>
                        </div>

                        <Alert 
                            message="Conseil Administrateur"
                            description="Utilisez le broadcast avec parcimonie pour ne pas saturer le fil de notifications des utilisateurs. Privilégiez des titres courts et percutants."
                            type="info"
                            showIcon
                            icon={<InfoCircleOutlined />}
                            className="mb-8 bg-indigo-500/5 border-indigo-500/20 text-indigo-200"
                        />

                        <Form
                            form={form}
                            layout="vertical"
                            onFinish={onFinish}
                            initialValues={{ target: 'all', type: 'info' }}
                        >
                            <Row gutter={24}>
                                <Col xs={24} md={12}>
                                    <Form.Item 
                                        name="target" 
                                        label={<Text className="text-white font-black uppercase tracking-widest text-[10px]">Cible de la notification</Text>}
                                        rules={[{ required: true }]}
                                    >
                                        <Select className="premium-select">
                                            <Select.Option value="all">
                                                <Space><GlobalOutlined /> Tous les utilisateurs (Broadcast)</Space>
                                            </Select.Option>
                                            <Select.Option value="user">
                                                <Space><UserOutlined /> Utilisateur spécifique</Space>
                                            </Select.Option>
                                        </Select>
                                    </Form.Item>

                                    <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.target !== currentValues.target}>
                                        {({ getFieldValue }) => 
                                            getFieldValue('target') === 'user' ? (
                                                <Form.Item 
                                                    name="userId" 
                                                    label={<Text className="text-white font-black uppercase tracking-widest text-[10px]">ID Utilisateur (UUID)</Text>}
                                                    rules={[{ required: true, message: 'Veuillez entrer l\'ID de l\'utilisateur' }]}
                                                >
                                                    <Input placeholder="ex: 550e8400-e29b-41d4-a716-446655440000" className="premium-input" />
                                                </Form.Item>
                                            ) : null
                                        }
                                    </Form.Item>

                                    <Form.Item 
                                        name="type" 
                                        label={<Text className="text-white font-black uppercase tracking-widest text-[10px]">Type / Style</Text>}
                                        rules={[{ required: true }]}
                                    >
                                        <Select className="premium-select">
                                            <Select.Option value="info"><Badge status="processing" text="Information (Bleu)" /></Select.Option>
                                            <Select.Option value="success"><Badge status="success" text="Succès (Vert)" /></Select.Option>
                                            <Select.Option value="warning"><Badge status="warning" text="Alerte (Orange)" /></Select.Option>
                                            <Select.Option value="error"><Badge status="error" text="Critique (Rouge)" /></Select.Option>
                                            <Select.Option value="broadcast"><Badge color="purple" text="Annonce Spéciale (Violet)" /></Select.Option>
                                        </Select>
                                    </Form.Item>
                                </Col>

                                <Col xs={24} md={12}>
                                    <Form.Item 
                                        name="title" 
                                        label={<Text className="text-white font-black uppercase tracking-widest text-[10px]">Titre de la notification</Text>}
                                        rules={[{ required: true, message: 'Le titre est obligatoire' }]}
                                    >
                                        <Input placeholder="ex: Maintenance programmée" className="premium-input" maxLength={100} />
                                    </Form.Item>

                                    <Form.Item 
                                        name="message" 
                                        label={<Text className="text-white font-black uppercase tracking-widest text-[10px]">Contenu du message</Text>}
                                        rules={[{ required: true, message: 'Le message est obligatoire' }]}
                                    >
                                        <TextArea rows={4} placeholder="Décrivez l'annonce ici..." className="premium-input" />
                                    </Form.Item>

                                    <Form.Item 
                                        name="link" 
                                        label={<Text className="text-white font-black uppercase tracking-widest text-[10px]">Lien d'action (optionnel)</Text>}
                                    >
                                        <Input placeholder="ex: /trade ou https://t.me/..." className="premium-input" />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Divider className="border-white/5" />

                            <div className="flex justify-end">
                                <Button 
                                    type="primary" 
                                    icon={<SendOutlined />} 
                                    htmlType="submit" 
                                    loading={loading}
                                    size="large"
                                    className="premium-button-indigo px-8"
                                >
                                    Envoyer la notification
                                </Button>
                            </div>
                        </Form>
                    </Card>
                </Col>

                <Col span={24}>
                    <Card className="premium-card bg-slate-900 border-white/5 shadow-2xl">
                        <Title level={4} style={{ color: 'white' }} className="uppercase tracking-tighter font-black mb-6">
                            Aperçu en temps réel
                        </Title>
                        <Form.Item noStyle shouldUpdate>
                            {({ getFieldsValue }) => {
                                const { title, message: msg, type } = getFieldsValue() || {};
                                if (!title && !msg) return <Empty description={<span className="text-slate-500">Remplissez le formulaire pour voir l'aperçu</span>} />;
                                
                                return (
                                    <div className={`p-6 rounded-[2rem] border ${
                                        type === 'success' ? 'bg-emerald-500/10 border-emerald-500/20' :
                                        type === 'error' ? 'bg-rose-500/10 border-rose-500/20' :
                                        type === 'warning' ? 'bg-amber-500/10 border-amber-500/20' :
                                        'bg-indigo-500/10 border-indigo-500/20'
                                    }`}>
                                        <div className="flex gap-4">
                                            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
                                                type === 'success' ? 'bg-emerald-500/20 text-emerald-400' :
                                                type === 'error' ? 'bg-rose-500/20 text-rose-400' :
                                                type === 'warning' ? 'bg-amber-500/20 text-amber-400' :
                                                'bg-indigo-500/20 text-indigo-400'
                                            }`}>
                                                {type === 'success' ? <CheckCircleOutlined size={24} /> :
                                                 type === 'error' ? <AlertOutlined size={24} /> :
                                                 type === 'warning' ? <AlertOutlined size={24} /> :
                                                 <NotificationOutlined size={24} />}
                                            </div>
                                            <div>
                                                <h4 className="text-lg font-black text-white m-0">{title || 'Titre de l\'exemple'}</h4>
                                                <Paragraph className="text-slate-400 mt-1 mb-0">{msg || 'Le contenu de votre notification s\'affichera ici.'}</Paragraph>
                                                <div className="mt-3">
                                                    <span className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">Maintenant</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            }}
                        </Form.Item>
                    </Card>
                </Col>
            </Row>

            <style dangerouslySetInnerHTML={{ __html: `
                .premium-card { border-radius: 2rem !important; }
                .premium-input, .premium-select .ant-select-selector {
                    background: rgba(255,255,255,0.03) !important;
                    border: 1px solid rgba(255,255,255,0.1) !important;
                    border-radius: 1rem !important;
                    color: white !important;
                }
                .premium-input:focus, .premium-input:hover {
                    border-color: #6366f1 !important;
                    background: rgba(255,255,255,0.05) !important;
                }
                .ant-form-item-label label { color: rgba(255,255,255,0.5) !important; }
                .premium-button-indigo {
                    background: #6366f1 !important;
                    border: none !important;
                    border-radius: 1rem !important;
                    height: auto !important;
                    padding: 12px 24px !important;
                    font-weight: 900 !important;
                    text-transform: uppercase !important;
                    letter-spacing: 0.1em !important;
                }
            `}} />
        </div>
    );
};

export default NotificationManagement;
